// dashboard/src/components/api-playground/WelcomeToPlaygroundCard.tsx
import React from 'react';

interface WelcomeToPlaygroundCardProps {
  fromApiKeys?: boolean;
  newApiKey?: boolean;
}

const WelcomeToPlaygroundCard: React.FC<WelcomeToPlaygroundCardProps> = ({ 
  fromApiKeys = false, 
  newApiKey = false 
}) => {
  return (
    <div className="bg-gradient-to-r from-purple-50 to-indigo-100 border border-purple-200 rounded-lg p-8 mb-6">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="bg-purple-500 rounded-full p-3">
            <span className="text-white text-3xl">🛠️</span>
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-purple-900 mb-3">
          {newApiKey ? "Perfect! Now Let's Test Your API" : "Welcome to the API Playground"}
        </h2>
        
        <p className="text-purple-800 text-lg mb-6 max-w-2xl mx-auto">
          {fromApiKeys 
            ? "Great job creating your API key! Now let's test it with your first API request. The playground makes it easy to experiment with our endpoints."
            : "Test and experiment with the Crawly Carl API in real-time. Perfect for learning our endpoints and building your integration."
          }
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border border-purple-200">
            <div className="text-purple-500 text-2xl mb-2">⚡</div>
            <h3 className="font-semibold text-purple-900 mb-1">Live Testing</h3>
            <p className="text-sm text-purple-700">Make real API calls and see responses instantly</p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-purple-200">
            <div className="text-blue-500 text-2xl mb-2">📝</div>
            <h3 className="font-semibold text-purple-900 mb-1">Code Examples</h3>
            <p className="text-sm text-purple-700">Get code snippets for your favorite language</p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-purple-200">
            <div className="text-green-500 text-2xl mb-2">🔍</div>
            <h3 className="font-semibold text-purple-900 mb-1">Response Explorer</h3>
            <p className="text-sm text-purple-700">Inspect and understand API responses</p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 border border-purple-200 mb-6">
          <h3 className="text-lg font-semibold text-purple-900 mb-4">Try Your First Request</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            <div className="flex items-start">
              <span className="text-purple-500 mr-3 mt-1">1️⃣</span>
              <div>
                <p className="font-medium text-purple-900">Select an Endpoint</p>
                <p className="text-sm text-purple-700">Choose from our available API endpoints</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-blue-500 mr-3 mt-1">2️⃣</span>
              <div>
                <p className="font-medium text-purple-900">Configure Parameters</p>
                <p className="text-sm text-purple-700">Set your target URL and options</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-green-500 mr-3 mt-1">3️⃣</span>
              <div>
                <p className="font-medium text-purple-900">Send Request</p>
                <p className="text-sm text-purple-700">Click send and see the magic happen</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-orange-500 mr-3 mt-1">4️⃣</span>
              <div>
                <p className="font-medium text-purple-900">Explore Response</p>
                <p className="text-sm text-purple-700">Analyze the crawled data and metadata</p>
              </div>
            </div>
          </div>
        </div>

        {newApiKey && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center mb-2">
              <span className="text-green-500 text-xl mr-2">✅</span>
              <h4 className="font-semibold text-green-900">API Key Ready!</h4>
            </div>
            <p className="text-sm text-green-800">
              Your API key has been automatically selected in the playground. You're ready to make your first request!
            </p>
          </div>
        )}

        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-center mb-2">
            <span className="text-yellow-500 text-lg mr-2">💡</span>
            <h4 className="font-semibold text-yellow-900">Pro Tip</h4>
          </div>
          <p className="text-sm text-yellow-800">
            Start with a simple URL like "https://example.com" to see how our API works. You can always try more complex sites later!
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeToPlaygroundCard;
